import AxiosService from '@/api/ApiBase';
import { IpiLoadPlan } from '@/types/ipi/load-plan/ipi.load-plan.entity';
import {
  IpiLoadModeChangePayload,
  IpiLoadPlanFilters,
  IpiLoadPlanPayload,
  IpiLoadPlansResponse,
  IpiLoadPlanViaStopCreatePayload,
  IpiLoadPlanViaStopResponse,
  IpiManualContainerPayload,
  LoadPlanLockStatusPayload,
  LoadPlanProfitLossResponse,
} from '@/types/ipi/load-plan/ipi.load-plan.types';
import { removeEmptyValuesFromObject } from '@/helpers/objects';
import {
  IpiInventoryResponse,
  IpiUpcomingInventoryFilters,
} from '@/types/ipi/inventory/ipi.inventory.types';
import { FilterOptionsParams, FiltersService } from '@/types/service/filters.service';
import { AxiosResponse } from 'axios';
import { VirtualSearchPayload, VirtualSearchResponse } from '@/types/virtual-search';
import {
  IpiLoadViaStopOrderPayload,
  IpiLoadViaStopOrderWithDestinationPayload,
} from '@/types/ipi/shared/ipi.load.via-stop.types';

const IPI_BASE_URL = 'warehouse/ipi';
const LOAD_PLANS_BASE_URL = 'warehouse/loadPlans';
const IPI_BASE_URL_1 = 'http://localhost:3000/api/ipi';
const IPI_LOAD_BASE_URL = IPI_BASE_URL + '/loadPlan';
const IPI_LOAD_NUMBER_LENGTH = 7;

class IpiLoadService implements FiltersService<IpiLoadPlan, IpiLoadPlanFilters> {
  generate() {
    return AxiosService.post<string>(`${IPI_BASE_URL}/generateLoad`);
  }

  getFilteredItems(
    filters: IpiLoadPlanFilters
  ): Promise<AxiosResponse<IpiLoadPlansResponse, unknown>> {
    return AxiosService.post(`${IPI_LOAD_BASE_URL}/getAll`, removeEmptyValuesFromObject(filters));
  }

  virtualSearch(payload: VirtualSearchPayload<IpiLoadPlan>) {
    return AxiosService.post<VirtualSearchResponse<IpiLoadPlan>>(
      'warehouse/v2/ipiLoading/loadPlan/search',
      payload
    );
  }

  getFilterOptions<T>(params: Omit<FilterOptionsParams, 'limit'>) {
    return AxiosService.get<T>(`${IPI_LOAD_BASE_URL}/filter/options`, {
      params: { ...params, limit: 100 },
    });
  }

  getAll(filters: IpiLoadPlanFilters) {
    return AxiosService.post<IpiLoadPlan[]>(
      `${IPI_LOAD_BASE_URL}/getAll`,
      removeEmptyValuesFromObject(filters)
    );
  }

  getById(id: string) {
    const param = id.length > IPI_LOAD_NUMBER_LENGTH ? 'id' : 'loadNumber';

    return AxiosService.get<IpiLoadPlan>(`${IPI_LOAD_BASE_URL}/get`, {
      params: { [param]: id },
    });
  }

  delete(id: string) {
    return AxiosService.delete(`${IPI_LOAD_BASE_URL}/delete/${id}`);
  }

  update(id: string, payload: Partial<IpiLoadPlan>) {
    return AxiosService.patch(`${IPI_LOAD_BASE_URL}/update/${id}`, payload);
  }

  getUpcoming(filters: IpiUpcomingInventoryFilters) {
    return AxiosService.get<IpiInventoryResponse>(`${IPI_BASE_URL}/upcomingLoads`, {
      params: filters,
    });
  }

  createFutureLoadPlans(startDate: string) {
    return AxiosService.post(`${IPI_LOAD_BASE_URL}/create/futureLoadPlans`, {
      startDate,
    });
  }

  updateLockStatus(payload: LoadPlanLockStatusPayload) {
    return AxiosService.put(`${IPI_LOAD_BASE_URL}/updateLockStatus`, payload);
  }

  createManual(payload: IpiManualContainerPayload) {
    return AxiosService.post(`${IPI_BASE_URL}/loadPlan/create/manual`, payload);
  }

  createGroupedContainers(payload: IpiLoadPlanPayload) {
    return AxiosService.post(`${IPI_LOAD_BASE_URL}/groupContainers`, payload);
  }

  updateViaStopsOrder({ loadId, viaWarehousesIds }: IpiLoadViaStopOrderPayload) {
    return AxiosService.put<IpiLoadPlanViaStopResponse>(
      `${IPI_LOAD_BASE_URL}/viaStops/order/${loadId}`,
      { viaWarehousesIds }
    );
  }

  updateViaStopsOrderWithDestination({
    loadId,
    ...payload
  }: IpiLoadViaStopOrderWithDestinationPayload) {
    return AxiosService.put<IpiLoadPlanViaStopResponse>(
      `${IPI_LOAD_BASE_URL}/v2/viaStops/order/${loadId}`,
      payload
    );
  }

  addViaStop(loadPlanId: string, payload: Omit<IpiLoadPlanViaStopCreatePayload, 'loadPlanId'>) {
    return AxiosService.post<IpiLoadPlanViaStopResponse>(
      `${IPI_LOAD_BASE_URL}/viaStops/${loadPlanId}`,
      payload
    );
  }

  deleteViaStop(loadPlanId: string, viaStopId: string) {
    return AxiosService.delete<IpiLoadPlanViaStopResponse>(
      `${IPI_LOAD_BASE_URL}/viaStops/${loadPlanId}/${viaStopId}`
    );
  }

  getProfitLoss(id: string) {
    return AxiosService.get<LoadPlanProfitLossResponse>(
      `${LOAD_PLANS_BASE_URL}/ProfitAndLoss?loadPlanId=${id}`
    );
  }

  changeLoadMode(payload: IpiLoadModeChangePayload) {
    return AxiosService.patch(`${IPI_LOAD_BASE_URL}/loadMode`, payload);
  }
}

export default new IpiLoadService();
